import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { WSProvider } from './context/WSContext';
import { WorkspaceProvider } from './context/WorkspaceContext';
import { MachineProvider } from './context/MachineContext';
import { TelemetryProvider as TelemetryProviderInner } from './context/TelemetryContext';
import { useMachines } from './context/MachineContext';
import { PlanProvider as PlanProviderInner } from './context/PlanContext';
import { useMachines as useMachinesForPlan } from './context/MachineContext';
import { TicketProvider } from './context/TicketContext';
import { TwinProvider } from './context/TwinContext';
import { useEWMAMonitor } from './utils/useEWMAMonitor';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import DemoBanner from './components/DemoBanner';
import Login from './pages/Login';
import AdminDashboard from './pages/AdminDashboard';
import Landing from './pages/Landing';
import Onboarding from './pages/Onboarding';
import TwinSuccess from './pages/TwinSuccess';
import ConnectTwin from './pages/ConnectTwin';
import Dashboard from './pages/Dashboard';
import Entities from './pages/Entities';
import MLPredictions from './pages/MLPredictions';
import Alerts from './pages/Alerts';
import Custom3DViewer from './pages/Custom3DViewer';
import GrafanaEmbed from './pages/GrafanaEmbed';
import Reports from './pages/Reports';
import Machines from './pages/Machines';
import DeviceConnect from './pages/DeviceConnect';
import LiveDashboard from './pages/LiveDashboard';
import Tickets from './pages/Tickets';
import MyTwins from './pages/MyTwins';
import ModelTest from './pages/ModelTest';
import ModelUpload from './pages/ModelUpload';
import './App.css';

const PlanProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { machines } = useMachinesForPlan();
  const sensorCounts = Object.fromEntries(machines.map(m => [m.machineId, m.sensors.length]));
  return <PlanProviderInner machineCount={machines.length} sensorCounts={sensorCounts}>{children}</PlanProviderInner>;
};

const TelemetryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { machines } = useMachines();
  return <TelemetryProviderInner machines={machines}>{children}</TelemetryProviderInner>;
};

const EWMAMonitor: React.FC = () => { useEWMAMonitor(); return null; };

const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute><Navbar /><DemoBanner />{children}</ProtectedRoute>
);

const App: React.FC = () => (
  <BrowserRouter>
    <ThemeProvider>
      <AuthProvider>
        <WorkspaceProvider>
          <TwinProvider>
          <EWMAMonitor />
          <MachineProvider>
          <PlanProvider>
          <TelemetryProvider>
          <TicketProvider>
          <WSProvider>
            <Routes>
              <Route path="/"            element={<Navigate to="/login" />} />
              {/* Auth routes */}
              <Route path="/login"       element={<Login />} />
              {/* Onboarding enabled for Get Started flow */}
              <Route path="/onboarding"  element={<Navigate to="/upload-model" />} />
              <Route path="/twin-success"  element={<AppLayout><TwinSuccess /></AppLayout>} />
              <Route path="/connect-twin"  element={<ProtectedRoute><ConnectTwin /></ProtectedRoute>} />
              <Route path="/upload-model"  element={<ProtectedRoute><ModelUpload /></ProtectedRoute>} />
              <Route path="/connect"     element={<Navigate to="/app" />} />
              <Route path="/pricing"     element={<Navigate to="/" />} />
              {/* App */}
              <Route path="/app"               element={<AppLayout><Dashboard />    </AppLayout>} />
              <Route path="/app/entities"      element={<AppLayout><Entities />     </AppLayout>} />
              <Route path="/app/ml"            element={<AppLayout><MLPredictions /></AppLayout>} />
              <Route path="/app/alerts"        element={<AppLayout><Alerts />             </AppLayout>} />
              <Route path="/app/scene"         element={<AppLayout><Custom3DViewer />  </AppLayout>} />
              <Route path="/app/grafana"       element={<AppLayout><GrafanaEmbed />    </AppLayout>} />
              <Route path="/app/reports"       element={<AppLayout><Reports />      </AppLayout>} />
              <Route path="/app/settings"      element={<Navigate to="/app" />} />
              <Route path="/app/machines"      element={<AppLayout><Machines />     </AppLayout>} />
              <Route path="/app/devices"       element={<AppLayout><DeviceConnect /></AppLayout>} />
              <Route path="/app/live"          element={<AppLayout><LiveDashboard /></AppLayout>} />
              <Route path="/app/billing"       element={<Navigate to="/app" />} />
              <Route path="/app/tickets"       element={<AppLayout><Tickets />      </AppLayout>} />
              <Route path="/app/twins"         element={<AppLayout><MyTwins />      </AppLayout>} />
              <Route path="/app/admin"         element={<AppLayout><AdminDashboard /></AppLayout>} />
              <Route path="/test/model"        element={<ModelTest />} />
              <Route path="*"                  element={<Navigate to="/login" />} />
            </Routes>
          </WSProvider>
          </TicketProvider>
          </TelemetryProvider>
          </PlanProvider>
          </MachineProvider>
          </TwinProvider>
        </WorkspaceProvider>
      </AuthProvider>
    </ThemeProvider>
  </BrowserRouter>
);

export default App;
